import { useEffect, useState, useCallback } from 'react'
import { useLoaderData, useParams } from 'react-router-dom'
import axios from 'axios'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Slider,
  TextField,
  Paper,
  Chip,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { ProfileModel, ActivityModel, ActivityHistoryModel } from 'types'
import { base_url } from 'env'
import ActivityHistoryDataGrid from 'components/ActivityHistoryDataGrid'
// Helper component for rating sliders
const RatingSlider = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (event: Event, value: number | number[]) => void
}) => (
  <Box sx={{ width: '100%', mt: 1 }}>
    <Typography gutterBottom>{label}</Typography>
    <Slider
      value={value}
      onChange={onChange}
      step={1}
      marks
      min={1}
      max={5}
      valueLabelDisplay="auto"
    />
  </Box>
)

// Initial state for the feedback form
const initialHistoryState: Omit<
  ActivityHistoryModel,
  'id' | 'profileId' | 'activityId' | 'date' | 'giftedPoints'
> = {
  length: 15,
  ratingDifficulty: 3,
  ratingEnjoyed: 3,
  ratingFocused: 3,
  ratingLength: 3,
}

export default function RecommenderPage() {
  const { profileId } = useParams<{ profileId: string }>()
  const initialProfile = useLoaderData() as ProfileModel

  const [profile, setProfile] = useState<ProfileModel>(initialProfile)
  const [activityDetails, setActivityDetails] = useState<ActivityModel | null>(
    null
  )
  const [historyForm, setHistoryForm] = useState(initialHistoryState)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchAndSetActivityDetails = useCallback(
    async (activityId: string | null) => {
      if (!activityId) {
        setActivityDetails(null)
        setIsLoading(false)
        return
      }
      const token = localStorage.getItem('token')
      try {
        const response = await axios.get(`${base_url}/activity/${activityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setActivityDetails(response.data)
      } catch (error) {
        console.error('Failed to fetch activity details', error)
        setActivityDetails(null)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    setIsLoading(true)
    fetchAndSetActivityDetails(profile.suggestedActivity)
  }, [profile, fetchAndSetActivityDetails])

  const handleSubmitFeedback = async () => {
    if (!activityDetails?.id) {
      alert('Aucune activité suggérée à évaluer.')
      return
    }
    setIsSubmitting(true)
    const token = localStorage.getItem('token')

    const payload = {
      ...historyForm,
      activityId: activityDetails.id,
      date: Math.floor(Date.now() / 1000), // Current timestamp
    }

    try {
      // Step 1: Submit the feedback. The backend should handle generating
      // a new suggestion after this POST completes.
      await axios.post(`${base_url}/profile/${profileId}/activity`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })

      // Step 2: Fetch the updated profile to get the new suggestedActivity ID.
      const updatedProfileRes = await axios.get(
        `${base_url}/profile/${profileId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      alert('Feedback enregistré ! Chargement de la prochaine suggestion.')
      setProfile(updatedProfileRes.data) // This will trigger the useEffect to fetch new activity details
      setHistoryForm(initialHistoryState) // Reset form
    } catch (error) {
      console.error('Failed to submit feedback and get new suggestion:', error)
      alert('Une erreur est survenue.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageAppBar pageName={`Test du Recommender pour ${profile.firstName}`}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Activité Suggérée
            </Typography>
            {isLoading ? (
              <CircularProgress />
            ) : activityDetails ? (
              <>
                <Typography variant="h6">{activityDetails.title}</Typography>
                <Chip
                  label={`Catégorie: ${activityDetails.category}`}
                  sx={{ my: 1 }}
                />
                <Chip
                  label={`Difficulté de base: ${activityDetails.baseDifficulty}`}
                  sx={{ my: 1, ml: 1 }}
                />
                <Typography variant="body1" color="text.secondary" paragraph>
                  {activityDetails.instructions}
                </Typography>
              </>
            ) : (
              <Typography>
                Aucune activité n'est suggérée pour le moment.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Paper sx={{ p: 3, width: '100%', maxWidth: '800px' }}>
          <Typography variant="h5" gutterBottom>
            Donner un Feedback
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Durée (en minutes)"
                type="number"
                value={historyForm.length}
                onChange={(e) =>
                  setHistoryForm({
                    ...historyForm,
                    length: Number(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RatingSlider
                label="Difficulté Ressentie"
                value={historyForm.ratingDifficulty}
                onChange={(e, v) =>
                  setHistoryForm({
                    ...historyForm,
                    ratingDifficulty: v as number,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RatingSlider
                label="Appréciation"
                value={historyForm.ratingEnjoyed}
                onChange={(e, v) =>
                  setHistoryForm({ ...historyForm, ratingEnjoyed: v as number })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RatingSlider
                label="Concentration"
                value={historyForm.ratingFocused}
                onChange={(e, v) =>
                  setHistoryForm({ ...historyForm, ratingFocused: v as number })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RatingSlider
                label="Évaluation de la durée"
                value={historyForm.ratingLength}
                onChange={(e, v) =>
                  setHistoryForm({ ...historyForm, ratingLength: v as number })
                }
              />
            </Grid>
          </Grid>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleSubmitFeedback}
            disabled={isSubmitting || isLoading || !activityDetails}
            sx={{ mt: 3, p: 1.5 }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              'Envoyer et Obtenir la Prochaine Suggestion'
            )}
          </Button>
        </Paper>
        <ActivityHistoryDataGrid profileId={profile.id} />
      </Box>
    </PageAppBar>
  )
}
